import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { toast } from '@/store/toastStore'
import type { CollaborationMutation, CollaborationWorkspace } from '@/types/returnCase.types'

export interface CollaborationClient {
  collaboration: (caseId: string) => Promise<CollaborationWorkspace>
  createQuery?: (caseId: string, input: Record<string, unknown>) => Promise<CollaborationMutation>
  queryCommand?: (
    caseId: string,
    queryId: string,
    command: string,
    input: Record<string, unknown>
  ) => Promise<CollaborationMutation>
  addQueryMessage?: (
    caseId: string,
    queryId: string,
    input: Record<string, unknown>
  ) => Promise<CollaborationMutation>
  createTask?: (caseId: string, input: Record<string, unknown>) => Promise<CollaborationMutation>
  taskCommand?: (
    caseId: string,
    taskId: string,
    command: string,
    input: Record<string, unknown>
  ) => Promise<CollaborationMutation>
  respondToQuery?: (
    caseId: string,
    queryId: string,
    input: Record<string, unknown>
  ) => Promise<CollaborationMutation>
}

interface Props {
  caseId: string
  client: CollaborationClient
  mode: 'staff' | 'customer'
  defaultOwnerId?: string
  canRespond?: boolean
  onChanged?: () => Promise<void> | void
}

const reason = (action: string) =>
  `${action} through the governed assessment collaboration workspace`

export default function CollaborationPanel({
  caseId,
  client,
  mode,
  defaultOwnerId = '',
  canRespond = false,
  onChanged,
}: Props) {
  const queryClient = useQueryClient()
  const key = [mode === 'staff' ? 'case-collaboration' : 'bounded-case-collaboration', caseId]
  const workspace = useQuery({ queryKey: key, queryFn: () => client.collaboration(caseId) })
  const [threadText, setThreadText] = useState<Record<string, string>>({})
  const [threadAttachment, setThreadAttachment] = useState<Record<string, string>>({})
  const [queryDraft, setQueryDraft] = useState({
    subject: '',
    question: '',
    ownerUserId: defaultOwnerId,
    targetUserId: '',
    visibility: 'CUSTOMER_VISIBLE',
    priority: 'NORMAL',
    anchorRef: 'case',
    dependencyKey: '',
  })
  const [taskDraft, setTaskDraft] = useState({
    title: '',
    description: '',
    ownerUserId: defaultOwnerId,
    dueAt: '',
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
    await onChanged?.()
  }
  const action = useMutation({
    mutationFn: async (operation: () => Promise<CollaborationMutation>) => operation(),
    onSuccess: async result => {
      await refresh()
      toast.success(`${result.resource_type.toLowerCase()} updated`)
    },
    onError: error =>
      toast.error(
        'Collaboration update rejected',
        error instanceof Error ? error.message : 'Reload and try again.'
      ),
  })

  const createQuery = (event: FormEvent) => {
    event.preventDefault()
    if (!client.createQuery || !workspace.data) return
    const customerVisible = queryDraft.visibility === 'CUSTOMER_VISIBLE'
    action.mutate(() =>
      client.createQuery!(caseId, {
        expected_revision: workspace.data!.case_revision,
        reason: reason('Create query'),
        visibility: queryDraft.visibility,
        category: 'CLARIFICATION',
        priority: queryDraft.priority,
        subject: queryDraft.subject,
        question: queryDraft.question,
        owner_user_id: queryDraft.ownerUserId,
        target_kind: customerVisible ? 'CUSTOMER' : 'STAFF',
        target_user_id: customerVisible ? queryDraft.targetUserId : undefined,
        anchor_type: 'CASE',
        anchor_ref: queryDraft.anchorRef,
        anchor_location: { label: 'Case collaboration' },
        dependency_keys: queryDraft.dependencyKey ? [queryDraft.dependencyKey] : [],
        sla_timezone: 'Asia/Kolkata',
      })
    )
  }
  const createTask = (event: FormEvent) => {
    event.preventDefault()
    if (!client.createTask || !workspace.data) return
    action.mutate(() =>
      client.createTask!(caseId, {
        expected_revision: workspace.data!.case_revision,
        reason: reason('Create task'),
        task_type: 'FOLLOW_UP',
        title: taskDraft.title,
        description: taskDraft.description,
        priority: 'NORMAL',
        owner_user_id: taskDraft.ownerUserId,
        due_at: taskDraft.dueAt ? new Date(taskDraft.dueAt).toISOString() : undefined,
        sla_timezone: 'Asia/Kolkata',
        anchor_type: 'CASE',
        anchor_ref: 'case',
      })
    )
  }

  if (workspace.isLoading)
    return (
      <section className="card mt-6 p-5" aria-busy="true">
        Loading collaboration workspace…
      </section>
    )
  if (workspace.isError || !workspace.data)
    return (
      <section className="card mt-6 p-5">
        <div className="alert alert-error" role="alert">
          Collaboration tasks could not be loaded.
        </div>
      </section>
    )
  const data = workspace.data

  return (
    <section className="card mt-6 p-5" aria-labelledby={`${mode}-collaboration-heading`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${mode}-collaboration-heading`} className="font-semibold">
            Queries and tasks
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Controlled queries keep their history and evidence. General case notes remain separate.
          </p>
        </div>
        <span className="badge badge-primary">Revision {data.case_revision}</span>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
        {Object.entries(data.counts).map(([label, count]) => (
          <div key={label} className="rounded border border-border p-2">
            <dt className="text-xs text-text-secondary">{label.replace(/_/g, ' ')}</dt>
            <dd className="text-lg font-semibold">{count}</dd>
          </div>
        ))}
      </dl>

      {mode === 'staff' && client.createQuery && (
        <form
          className="mt-5 grid gap-3 rounded border border-border p-4 sm:grid-cols-2"
          onSubmit={createQuery}
        >
          <h3 className="font-medium sm:col-span-2">Create a controlled query</h3>
          <label className="text-sm">
            Subject
            <input
              className="input mt-1 w-full"
              value={queryDraft.subject}
              onChange={e => setQueryDraft({ ...queryDraft, subject: e.target.value })}
              minLength={3}
              required
            />
          </label>
          <label className="text-sm">
            Visibility
            <select
              className="input mt-1 w-full"
              value={queryDraft.visibility}
              onChange={e => setQueryDraft({ ...queryDraft, visibility: e.target.value })}
            >
              <option value="CUSTOMER_VISIBLE">Customer visible</option>
              <option value="INTERNAL">Internal only</option>
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            Question
            <textarea
              className="input mt-1 min-h-24 w-full"
              value={queryDraft.question}
              onChange={e => setQueryDraft({ ...queryDraft, question: e.target.value })}
              minLength={5}
              required
            />
          </label>
          <label className="text-sm">
            Owner user ID
            <input
              className="input mt-1 w-full"
              value={queryDraft.ownerUserId}
              onChange={e => setQueryDraft({ ...queryDraft, ownerUserId: e.target.value })}
              required
            />
          </label>
          {queryDraft.visibility === 'CUSTOMER_VISIBLE' && (
            <label className="text-sm">
              Customer user ID
              <input
                className="input mt-1 w-full"
                value={queryDraft.targetUserId}
                onChange={e => setQueryDraft({ ...queryDraft, targetUserId: e.target.value })}
                required
              />
            </label>
          )}
          <label className="text-sm">
            Anchor reference
            <input
              className="input mt-1 w-full"
              value={queryDraft.anchorRef}
              onChange={e => setQueryDraft({ ...queryDraft, anchorRef: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            Dependency key
            <input
              className="input mt-1 w-full"
              value={queryDraft.dependencyKey}
              onChange={e => setQueryDraft({ ...queryDraft, dependencyKey: e.target.value })}
              placeholder="fact:salary.basic"
            />
          </label>
          <button className="btn btn-primary sm:col-span-2" disabled={action.isPending}>
            Create draft query
          </button>
        </form>
      )}

      <div className="mt-5 space-y-4">
        {data.queries.length === 0 && (
          <p className="text-sm text-text-secondary">
            No controlled queries are currently visible to you.
          </p>
        )}
        {data.queries.map(query => (
          <article
            key={query.id}
            className={`rounded border p-4 ${query.overdue ? 'border-error-500' : 'border-border'}`}
            aria-labelledby={`query-${query.id}`}
          >
            <div className="flex flex-wrap justify-between gap-2">
              <h3 id={`query-${query.id}`} className="font-medium">
                {query.subject}
              </h3>
              <div className="flex gap-2">
                <span className="badge">{query.status.replace(/_/g, ' ')}</span>
                {mode === 'staff' && (
                  <span className="badge">
                    {query.visibility === 'INTERNAL' ? 'Internal' : 'Customer visible'}
                  </span>
                )}
              </div>
            </div>
            <p className="mt-2 text-sm">{query.question}</p>
            <p className="mt-1 text-xs text-text-secondary">
              Anchor: {query.anchor_type.toLowerCase()} · {query.anchor_ref} · SLA{' '}
              {query.sla_state ?? 'not started'}
            </p>
            <ol className="mt-3 space-y-2" aria-label="Query conversation">
              {query.messages.map(message => (
                <li key={message.id} className="rounded bg-surface-secondary p-3 text-sm">
                  <div className="text-xs text-text-secondary">
                    {message.actor_kind.toLowerCase()} · {message.message_kind.toLowerCase()} ·
                    revision {message.case_revision}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap">{message.body}</p>
                  {message.attachment_binding_ids.length > 0 && (
                    <ul className="mt-2 text-xs text-text-secondary" aria-label="Message evidence">
                      {message.attachment_binding_ids.map(bindingId => (
                        <li key={bindingId}>Evidence binding: {bindingId}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
            {query.status === 'RESOLVED' && (
              <p className="mt-3 rounded bg-success-50 p-3 text-sm">
                Resolution: {query.resolution_summary}
              </p>
            )}
            {((mode === 'customer' &&
              canRespond &&
              ['OPEN', 'REOPENED', 'NEEDS_MORE_INFO'].includes(query.status)) ||
              mode === 'staff') && (
              <div className="mt-3 space-y-2">
                <label className="text-sm" htmlFor={`query-action-${query.id}`}>
                  {mode === 'customer' ? 'Your response' : 'Action note or message'}
                </label>
                <textarea
                  id={`query-action-${query.id}`}
                  className="input min-h-20 w-full"
                  value={threadText[query.id] ?? ''}
                  onChange={e => setThreadText({ ...threadText, [query.id]: e.target.value })}
                />
                <label className="text-sm" htmlFor={`query-attachment-${query.id}`}>
                  Evidence binding ID (optional)
                </label>
                <input
                  id={`query-attachment-${query.id}`}
                  className="input w-full"
                  value={threadAttachment[query.id] ?? ''}
                  onChange={e =>
                    setThreadAttachment({ ...threadAttachment, [query.id]: e.target.value })
                  }
                  placeholder="Existing case document binding UUID"
                />
                <div className="flex flex-wrap gap-2">
                  {mode === 'customer' && client.respondToQuery && (
                    <button
                      className="btn btn-primary"
                      disabled={action.isPending || !(threadText[query.id] ?? '').trim()}
                      onClick={() =>
                        action.mutate(() =>
                          client.respondToQuery!(caseId, query.id, {
                            expected_revision: data.case_revision,
                            reason: reason('Submit customer response'),
                            body: threadText[query.id],
                            attachment_binding_ids: (threadAttachment[query.id] ?? '').trim()
                              ? [(threadAttachment[query.id] ?? '').trim()]
                              : [],
                          })
                        )
                      }
                    >
                      Send response for CA review
                    </button>
                  )}
                  {mode === 'staff' && client.queryCommand && query.status === 'DRAFT' && (
                    <button
                      className="btn btn-primary"
                      disabled={action.isPending}
                      onClick={() =>
                        action.mutate(() =>
                          client.queryCommand!(caseId, query.id, 'submit', {
                            expected_revision: data.case_revision,
                            reason: reason('Submit query'),
                          })
                        )
                      }
                    >
                      Open query
                    </button>
                  )}
                  {mode === 'staff' &&
                    client.addQueryMessage &&
                    !['RESOLVED', 'WITHDRAWN'].includes(query.status) && (
                      <button
                        className="btn btn-secondary"
                        disabled={action.isPending || !(threadText[query.id] ?? '').trim()}
                        onClick={() =>
                          action.mutate(() =>
                            client.addQueryMessage!(caseId, query.id, {
                              expected_revision: data.case_revision,
                              reason: reason('Add query message'),
                              body: threadText[query.id],
                              visibility: query.visibility,
                              attachment_binding_ids: (threadAttachment[query.id] ?? '').trim()
                                ? [(threadAttachment[query.id] ?? '').trim()]
                                : [],
                            })
                          )
                        }
                      >
                        Add message
                      </button>
                    )}
                  {mode === 'staff' &&
                    client.queryCommand &&
                    query.status === 'AWAITING_CA_REVIEW' && (
                      <>
                        <button
                          className="btn btn-primary"
                          disabled={
                            action.isPending || (threadText[query.id] ?? '').trim().length < 10
                          }
                          onClick={() =>
                            action.mutate(() =>
                              client.queryCommand!(caseId, query.id, 'resolve', {
                                expected_revision: data.case_revision,
                                reason: reason('Resolve query'),
                                resolution_summary: threadText[query.id],
                              })
                            )
                          }
                        >
                          Resolve after review
                        </button>
                        <button
                          className="btn btn-secondary"
                          disabled={
                            action.isPending || (threadText[query.id] ?? '').trim().length < 5
                          }
                          onClick={() =>
                            action.mutate(() =>
                              client.queryCommand!(caseId, query.id, 'request-more-info', {
                                expected_revision: data.case_revision,
                                reason: reason('Request more information'),
                                request: threadText[query.id],
                              })
                            )
                          }
                        >
                          Request more information
                        </button>
                      </>
                    )}
                  {mode === 'staff' && client.queryCommand && query.status === 'RESOLVED' && (
                    <button
                      className="btn btn-secondary"
                      disabled={action.isPending}
                      onClick={() =>
                        action.mutate(() =>
                          client.queryCommand!(caseId, query.id, 'reopen', {
                            expected_revision: data.case_revision,
                            reason: reason('Reopen query'),
                          })
                        )
                      }
                    >
                      Reopen
                    </button>
                  )}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>

      {mode === 'staff' && client.createTask && (
        <form
          className="mt-6 grid gap-3 rounded border border-border p-4 sm:grid-cols-2"
          onSubmit={createTask}
        >
          <h3 className="font-medium sm:col-span-2">Assign a case task</h3>
          <label className="text-sm">
            Title
            <input
              className="input mt-1 w-full"
              value={taskDraft.title}
              onChange={e => setTaskDraft({ ...taskDraft, title: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            Owner user ID
            <input
              className="input mt-1 w-full"
              value={taskDraft.ownerUserId}
              onChange={e => setTaskDraft({ ...taskDraft, ownerUserId: e.target.value })}
              required
            />
          </label>
          <label className="text-sm sm:col-span-2">
            Description
            <textarea
              className="input mt-1 w-full"
              value={taskDraft.description}
              onChange={e => setTaskDraft({ ...taskDraft, description: e.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            Due date and time
            <input
              className="input mt-1 w-full"
              type="datetime-local"
              value={taskDraft.dueAt}
              onChange={e => setTaskDraft({ ...taskDraft, dueAt: e.target.value })}
            />
          </label>
          <button className="btn btn-primary sm:col-span-2" disabled={action.isPending}>
            Create task
          </button>
        </form>
      )}
      <div className="mt-4 space-y-2">
        {data.tasks.map(task => (
          <article
            key={task.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded border border-border p-3"
          >
            <div>
              <h3 className="font-medium">{task.title}</h3>
              <p className="text-sm text-text-secondary">
                {task.status.replace(/_/g, ' ')} · owner {task.owner_user_id}
                {task.overdue ? ' · overdue' : ''}
              </p>
            </div>
            {mode === 'staff' &&
              client.taskCommand &&
              !['COMPLETED', 'CANCELLED'].includes(task.status) && (
                <button
                  className="btn btn-secondary"
                  disabled={action.isPending}
                  onClick={() =>
                    action.mutate(() =>
                      client.taskCommand!(caseId, task.id, 'complete', {
                        expected_revision: data.case_revision,
                        reason: reason('Complete task'),
                        completion_evidence:
                          'Completion recorded by the assigned staff member in the governed task workspace.',
                      })
                    )
                  }
                >
                  Complete with audit note
                </button>
              )}
          </article>
        ))}
      </div>
    </section>
  )
}
